import "./styles.scss";
import React, { useContext } from "react";
import {
  FieldArrayWithId,
  FieldError,
  FieldPath,
  useFieldArray,
  UseFieldArrayAppend,
  UseFieldArrayReplace,
  useFormContext,
} from "react-hook-form";
import { PlayerCardWidgetOwner, PolymorphicPlayerCard } from "../../../../../../services/bedrock";
import { Button, Icon, Label, Popup } from "semantic-ui-react";
import { PlayerCardSettingsContext } from "../../../../Contexts";

export type PlayerCardFieldBaseProps = {
  index: number;
  fieldLabel: JSX.Element | string;
  fieldName: FieldPath<PlayerCardWidgetOwner>;
};

export const PlayerCardFieldBase = ({ index, fieldName, fieldLabel }: PlayerCardFieldBaseProps) => {
  const { User } = useContext(PlayerCardSettingsContext);
  const { control, setValue, getValues } = useFormContext<PolymorphicPlayerCard>();
  const isTemplate = !!getValues("coachId");

  var templateFieldIndex = -1;
  var isLocked = false;
  var templateValue: any;
  var fieldValue = getValues(`widgets.${index}.owner.${fieldName}`);
  const widgetType = getValues(`widgets.${index}.owner.type`);
  const supportsTemplateLock = ["Header", "Footer"].includes(widgetType);

  var templateFields:
    | FieldArrayWithId<PolymorphicPlayerCard, `widgets.${number}.templateFields`, "formId">[]
    | undefined;
  var appendTemplateField: UseFieldArrayAppend<PolymorphicPlayerCard, `widgets.${number}.templateFields`> | undefined;
  var replaceTemplateFields:
    | UseFieldArrayReplace<PolymorphicPlayerCard, `widgets.${number}.templateFields`>
    | undefined;
  if (isTemplate) {
    const { fields, append, replace } = useFieldArray({
      control,
      keyName: "formId",
      name: `widgets.${index}.templateFields`,
    });
    templateFields = fields;
    appendTemplateField = append;
    replaceTemplateFields = replace;
    templateFieldIndex = templateFields.findIndex(field => field.fieldName === fieldName);
    if (templateFieldIndex >= 0) {
      isLocked = templateFields[templateFieldIndex].isLocked;
    }
  } else if (supportsTemplateLock) {
    const template = getValues("template");
    const templateWidget = template?.widgets.find(widget => widget.owner.type === widgetType);
    if (templateWidget) {
      templateValue = (templateWidget.owner as any)[fieldName];
      templateFieldIndex = templateWidget.templateFields.findIndex(field => field.fieldName === fieldName);
      if (templateFieldIndex >= 0) {
        isLocked = templateWidget.templateFields[templateFieldIndex].isLocked;
      }
    }
  }

  const getErrorComponent = (error: FieldError | undefined) => {
    if (!error?.message) return null;
    return (
      <Label basic prompt pointing>
        {error.message}
      </Label>
    );
  };

  const handleLockButtonClick = () => {
    if (!templateFields || !appendTemplateField || !replaceTemplateFields) return;
    if (isLocked) {
      if (templateFieldIndex >= 0) {
        replaceTemplateFields(templateFields.filter((_, i) => i !== templateFieldIndex));
      }
    } else {
      if (templateFieldIndex >= 0) {
        setValue(`widgets.${index}.templateFields.${templateFieldIndex}.isLocked`, true);
      } else {
        appendTemplateField({ id: -Date.now(), fieldName, isLocked: true });
      }
    }
  };

  const getTemplateLockButton = () => {
    return (
      isTemplate &&
      supportsTemplateLock && (
        <Popup
          content="Locking a field will prevent its value from being overwritten by Players."
          trigger={
            <Button
              icon={isLocked ? "lock" : "unlock"}
              onClick={User.hasWritePermission ? handleLockButtonClick : undefined}
              className={`iconButton ${isLocked ? "selectedIconButton" : ""}`}
            />
          }
        />
      )
    );
  };

  const getLockedTemplateAlert = () => {
    if (!isLocked) return null;
    return (
      <div className="alert">
        <Popup
          content="A default value has been locked to this field and will override your changes."
          trigger={<Icon name="exclamation triangle" color="yellow" />}
        />
      </div>
    );
  };

  const useDefaultValueButton = (revertToTemplateValue: () => void) => {
    if (!User.hasWritePermission) return null;
    return (
      <Button
        size="mini"
        className="useDefaultValueButton"
        onClick={revertToTemplateValue}
        content="Use Default Value"
      />
    );
  };

  return {
    control,
    getValues,
    isTemplate,
    fieldValue,
    templateValue,
    getErrorComponent,
    getTemplateLockButton,
    getLockedTemplateAlert,
    useDefaultValueButton,
  };
};
